import React, { useEffect, useMemo, useRef, useState } from "react";
import { SubNav, FileDropzone, GlassCard } from "../gc/ui";
import AudioPanel from "./AudioPanel";
import { AUDIO_STUDIOS, AUDIO_TAGS, DEFAULT_SOUNDBOARD } from "../../lib/gamecraft";
import { audioApi, ai as aiApi } from "../../lib/api";
import { toast } from "sonner";
import {
  MagnifyingGlass, DownloadSimple, FloppyDisk, Play, Pause, Microphone,
  Sparkle, SpeakerHigh, ArrowSquareOut, MusicNotes, Waves, Plus, PencilSimple, Trash,
} from "@phosphor-icons/react";

const CAT_MAP = { sfx: "SFX", foley: "FOLEY", music: "BGM", ambient: "AMBIENCE", voice: "CUSTOM" };

function ensureCtx(ref) {
  if (!ref.current) ref.current = new (window.AudioContext || window.webkitAudioContext)();
  return ref.current;
}

function beep(ctx, { f, type, t }) {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = type;
  o.frequency.setValueAtTime(f, ctx.currentTime);
  o.frequency.exponentialRampToValueAtTime(Math.max(40, f * 0.4), ctx.currentTime + t);
  g.gain.setValueAtTime(0.18, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t);
  o.connect(g);
  g.connect(ctx.destination);
  o.start();
  o.stop(ctx.currentTime + t + 0.02);
}

export default function AudioHub({ project, onChange }) {
  const [studioId, setStudioId] = useState("sfx");
  const [sub, setSub] = useState("library");
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [links, setLinks] = useState([]);
  const [searching, setSearching] = useState(false);
  const [ideas, setIdeas] = useState("");
  const [pitch, setPitch] = useState(1);
  const [loop, setLoop] = useState(false);
  const [mood, setMood] = useState({ low: 40, mid: 55, high: 70, space: 20 });
  const [recording, setRecording] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [editingClip, setEditingClip] = useState(null);
  const [editingPad, setEditingPad] = useState(null);
  const audioRef = useRef(null);
  const ctxRef = useRef(null);
  const recRef = useRef(null);
  const [tagFilter, setTagFilter] = useState("all");
  const studio = AUDIO_STUDIOS.find((s) => s.id === studioId) || AUDIO_STUDIOS[0];
  const samples = project.samples || [];
  const filtered = samples.filter((s) => {
    const cat = (s.category || "SFX").toUpperCase();
    const tag = s.tag || "";
    let inStudio = false;
    if (s.studio) inStudio = s.studio === studioId;
    else if (studioId === "sfx") inStudio = (cat === "SFX" || cat === "CUSTOM") && !["pickup", "throw", "use"].includes(tag);
    else if (studioId === "foley") inStudio = cat === "FOLEY" || ["pickup", "throw", "use", "ui"].includes(tag);
    else if (studioId === "music") inStudio = cat === "BGM";
    else if (studioId === "ambient") inStudio = cat === "AMBIENCE";
    else if (studioId === "voice") inStudio = cat === "CUSTOM" || /voice|voz|dialog/i.test(s.name || "");
    if (!inStudio) return false;
    if (tagFilter !== "all" && tag !== tagFilter) return false;
    return true;
  });

  const pads = (project.soundboard && project.soundboard.length)
    ? project.soundboard
    : DEFAULT_SOUNDBOARD;
  const savePads = (next) => onChange({ soundboard: next });
  const saveSamples = (next) => onChange({ samples: next });

  const playPad = (pad) => {
    if (pad.mode === "clip") {
      const url = pad.data_url || samples.find((s) => s.id === pad.clip_id)?.data_url;
      if (url) {
        playUrl(url, pad.id);
        return;
      }
      toast.info("Asigna un clip a este pad");
      return;
    }
    beep(ensureCtx(ctxRef), {
      f: Number(pad.f) || 440,
      type: pad.type || "square",
      t: Number(pad.t) || 0.15,
    });
  };

  const playUrl = (url, id) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    const a = new Audio(url);
    a.playbackRate = pitch;
    a.loop = loop;
    a.onended = () => setPlayingId(null);
    a.play().catch(() => toast.error("No se pudo reproducir"));
    audioRef.current = a;
    setPlayingId(id);
  };

  const stopPlay = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlayingId(null);
  };

  const addClip = (clip) => {
    saveSamples([...(project.samples || []), { tag: (studio.tags || [])[0] || "", ...clip }]);
    toast.success(`Guardado: ${clip.name}`);
  };

  const upload = async (files) => {
    for (const file of files) {
      const data_url = await new Promise((res) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.readAsDataURL(file);
      });
      addClip({
        id: crypto.randomUUID(),
        name: file.name,
        duration: 0,
        sizeKB: Math.round(file.size / 1024),
        category: CAT_MAP[studioId] || "SFX",
        color: studio.color,
        data_url,
        studio: studioId,
        license: "own",
      });
    }
  };

  const searchWeb = async () => {
    setSearching(true);
    try {
      const data = await audioApi.search(q || studio.label, studio.query);
      setHits(data.results || []);
      setLinks(data.links || []);
      if (!(data.results || []).length) toast.info("Sin resultados CC. Prueba otra palabra.");
    } catch {
      toast.error("Búsqueda web no disponible (¿backend activo?)");
      setHits([]);
      setLinks([]);
    } finally {
      setSearching(false);
    }
  };

  const importHit = async (hit) => {
    if (!hit.url) {
      toast.error("Este resultado no tiene archivo descargable");
      return;
    }
    try {
      const file = await audioApi.fetch(hit.url, hit.title);
      addClip({
        id: crypto.randomUUID(),
        name: file.name,
        duration: 0,
        sizeKB: file.sizeKB,
        category: CAT_MAP[studioId] || "SFX",
        color: studio.color,
        data_url: file.data_url,
        studio: studioId,
        license: hit.license,
        source: hit.foreign_landing_url || hit.url,
        creator: hit.creator,
      });
    } catch {
      window.open(hit.foreign_landing_url || hit.url, "_blank", "noopener");
      toast.info("No se pudo importar directo. Abrí la fuente CC.");
    }
  };

  const askIdeas = async () => {
    try {
      const r = await aiApi.ideas({
        prompt: `Dame 8 ideas concretas de ${studio.label.toLowerCase()} para un juego 2D ${project.genre || "platformer"} llamado "${project.name}". Prompt de búsqueda: "${q || "atmosfera"}". Incluye mood, uso en gameplay y 1 frase de búsqueda web.`,
        category: "audio",
        language: "es",
      });
      setIdeas(r.text || "");
    } catch {
      toast.error("IA de ideas no disponible");
    }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        const data_url = await new Promise((res) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.readAsDataURL(blob);
        });
        addClip({
          id: crypto.randomUUID(),
          name: `take-${studioId}-${Date.now()}`,
          duration: 0,
          sizeKB: Math.round(blob.size / 1024),
          category: CAT_MAP[studioId],
          color: studio.color,
          data_url,
          studio: studioId,
          license: "own",
        });
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Micrófono no permitido");
    }
  };

  const stopRec = () => {
    recRef.current?.stop();
    recRef.current = null;
    setRecording(false);
  };

  useEffect(() => () => {
    try {
      audioRef.current?.pause();
    } catch {
      /* ignore */
    }
    audioRef.current = null;
  }, []);

  const eqStyle = useMemo(
    () => ({
      filter: `contrast(${0.8 + mood.mid / 200}) saturate(${0.7 + mood.high / 140})`,
    }),
    [mood]
  );

  return (
    <div className="h-full flex flex-col" style={eqStyle}>
      <div className="p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {AUDIO_STUDIOS.map((s) => (
            <button
              key={s.id}
              onClick={() => { setStudioId(s.id); setSub("library"); setTagFilter("all"); }}
              className="px-3 py-2 rounded-2xl text-left border min-w-[150px] gc-card-hover"
              style={{
                borderColor: studioId === s.id ? s.color : "var(--gc-border)",
                background: studioId === s.id ? "var(--gc-primary-soft)" : "var(--gc-surface)",
              }}
            >
              <div className="text-sm font-semibold flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                {s.label}
              </div>
              <div className="text-[11px] mt-0.5" style={{ color: "var(--gc-muted)" }}>{s.hint}</div>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SubNav
            items={[
              { id: "library", label: "Biblioteca" },
              { id: "web", label: "Buscar en la web" },
              { id: "pads", label: "Soundboard" },
              { id: "mixer", label: "Mood mixer" },
              { id: "seq", label: "Secuenciador" },
            ]}
            value={sub}
            onChange={setSub}
          />
          <FileDropzone accept="audio/*" multiple onFiles={upload} label="Subir audio" />
        </div>
      </div>

      <div className="flex-1 overflow-auto px-4 pb-6 space-y-4">
        {sub === "library" && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <button className="gc-btn gc-btn-ghost h-9 px-3 text-xs" onClick={recording ? stopRec : startRec}>
                <Microphone size={14} className={recording ? "text-rose-500" : ""} />
                {recording ? "Detener toma" : "Grabar mic"}
              </button>
              <label className="text-xs flex items-center gap-2" style={{ color: "var(--gc-muted)" }}>
                Pitch
                <input type="range" min="0.5" max="1.8" step="0.05" value={pitch} onChange={(e) => setPitch(Number(e.target.value))} />
                {pitch.toFixed(2)}x
              </label>
              <label className="text-xs flex items-center gap-2">
                <input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} /> Loop
              </label>
              <span className="text-[11px] uppercase tracking-widest" style={{ color: "var(--gc-muted)" }}>
                {studio.label} · {filtered.length} clips
              </span>
              <div className="flex flex-wrap gap-1">
                <button className={"gc-chip " + (tagFilter === "all" ? "gc-chip-on" : "")} onClick={() => setTagFilter("all")}>Todas</button>
                {(studio.tags || AUDIO_TAGS.map((x) => x.id)).map((id) => {
                  const label = AUDIO_TAGS.find((t) => t.id === id)?.label || id;
                  return (
                    <button key={id} className={"gc-chip " + (tagFilter === id ? "gc-chip-on" : "")} onClick={() => setTagFilter(id)}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            {filtered.length === 0 ? (
              <GlassCard>
                <p className="text-sm" style={{ color: "var(--gc-muted)" }}>
                  Aún no hay clips en esta sección. Sube archivos, graba o busca Creative Commons.
                </p>
              </GlassCard>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {filtered.map((clip) => (
                  <GlassCard key={clip.id} className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-semibold text-sm">{clip.name}</div>
                        <div className="text-[11px]" style={{ color: "var(--gc-muted)" }}>
                          {clip.category} · {clip.sizeKB || "?"} KB · {clip.license || "own"}
                        </div>
                      </div>
                      <span className="gc-eq" aria-hidden><i /><i /><i /><i /></span>
                    </div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      <button className="gc-btn gc-btn-primary h-8 px-3 text-xs" onClick={() => (playingId === clip.id ? stopPlay() : playUrl(clip.data_url, clip.id))}>
                        {playingId === clip.id ? <Pause size={12} /> : <Play size={12} />} Preview
                      </button>
                      <button className="gc-btn gc-btn-ghost h-8 px-3 text-xs" onClick={() => setEditingClip(clip.id)}>
                        <PencilSimple size={12} /> Editar
                      </button>
                      <a className="gc-btn gc-btn-ghost h-8 px-3 text-xs" href={clip.data_url} download={clip.name}>
                        <DownloadSimple size={12} />
                      </a>
                      <button
                        className="gc-btn gc-btn-ghost h-8 px-3 text-xs text-rose-500"
                        onClick={() => saveSamples(samples.filter((x) => x.id !== clip.id))}
                      >
                        <Trash size={12} /> Quitar
                      </button>
                    </div>
                    {editingClip === clip.id && (
                      <div className="mt-2 space-y-2">
                        <input
                          className="gc-input h-8 px-2 text-xs w-full"
                          value={clip.name}
                          onChange={(e) => saveSamples(samples.map((s) => s.id === clip.id ? { ...s, name: e.target.value } : s))}
                        />
                        <select
                          className="gc-input h-8 px-2 text-xs w-full"
                          value={clip.category || "SFX"}
                          onChange={(e) => saveSamples(samples.map((s) => s.id === clip.id ? { ...s, category: e.target.value } : s))}
                        >
                          <option value="SFX">SFX combate</option>
                          <option value="FOLEY">SFX mundo</option>
                          <option value="BGM">Música</option>
                          <option value="AMBIENCE">Ambiente</option>
                          <option value="CUSTOM">Voz / custom</option>
                        </select>
                        <select
                          className="gc-input h-8 px-2 text-xs w-full"
                          value={clip.tag || ""}
                          onChange={(e) => saveSamples(samples.map((s) => s.id === clip.id ? { ...s, tag: e.target.value } : s))}
                        >
                          <option value="">Sin etiqueta</option>
                          {AUDIO_TAGS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                        </select>
                        <button className="gc-chip" onClick={() => setEditingClip(null)}>Listo</button>
                      </div>
                    )}
                  </GlassCard>
                ))}
              </div>
            )}
          </>
        )}

        {sub === "web" && (
          <div className="space-y-3">
            <div className="gc-card p-4">
              <div className="text-sm font-semibold mb-1">Recopilar audio Creative Commons</div>
              <p className="text-xs mb-3" style={{ color: "var(--gc-muted)" }}>
                Búsqueda legal vía Openverse (CC0 / CC-BY). Úsalo como referencia o impórtalo citando al autor. No es scraping de catálogos de pago.
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  { t: "Openverse", u: `https://openverse.org/search/audio?q=${encodeURIComponent(q || studio.query)}` },
                  { t: "OpenGameArt", u: `https://opengameart.org/art-search-advanced?keys=${encodeURIComponent(q || studio.query)}&field_art_type_tid=12` },
                  { t: "Freesound", u: `https://freesound.org/search/?q=${encodeURIComponent(q || studio.query)}` },
                  { t: "Jamendo", u: `https://www.jamendo.com/search?qs=q=${encodeURIComponent(q || "chiptune")}` },
                ].map((x) => (
                  <a key={x.t} className="gc-chip" href={x.u} target="_blank" rel="noreferrer">{x.t}</a>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <input
                  className="gc-input h-10 px-3 flex-1 min-w-[200px]"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchWeb()}
                  placeholder={`Ej: lava dungeon ${studio.query}`}
                />
                <button className="gc-btn gc-btn-primary h-10 px-4" onClick={searchWeb} disabled={searching}>
                  <MagnifyingGlass size={16} /> {searching ? "Buscando…" : "Buscar"}
                </button>
                <button className="gc-btn gc-btn-ghost h-10 px-4" onClick={askIdeas}>
                  <Sparkle size={16} /> Ideas IA
                </button>
              </div>
            </div>
            {ideas && (
              <GlassCard className="p-4 whitespace-pre-wrap text-sm leading-relaxed">{ideas}</GlassCard>
            )}
            {!!links.length && (
              <div className="flex flex-wrap gap-2">
                {links.map((l) => (
                  <a key={l.url} className="gc-chip" href={l.url} target="_blank" rel="noreferrer">{l.title}</a>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {hits.map((hit) => (
                <GlassCard key={hit.id} className="p-3">
                  <div className="font-semibold text-sm line-clamp-1">{hit.title}</div>
                  <div className="text-[11px] mt-1" style={{ color: "var(--gc-muted)" }}>
                    {hit.creator} · {hit.license} · {hit.source}
                  </div>
                  {hit.url && (
                    <audio className="w-full mt-2 h-8" controls preload="none" src={hit.url} />
                  )}
                  <div className="flex gap-2 mt-2">
                    <button className="gc-btn gc-btn-primary h-8 px-3 text-xs" onClick={() => importHit(hit)}>
                      <FloppyDisk size={12} /> Guardar en proyecto
                    </button>
                    {hit.foreign_landing_url && (
                      <a className="gc-btn gc-btn-ghost h-8 px-3 text-xs" href={hit.foreign_landing_url} target="_blank" rel="noreferrer">
                        <ArrowSquareOut size={12} /> Fuente
                      </a>
                    )}
                  </div>
                </GlassCard>
              ))}
            </div>
          </div>
        )}

        {sub === "pads" && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm flex-1" style={{ color: "var(--gc-muted)" }}>
                Pads para probar SFX propios. Clic = play. Edita, asigna un clip o un sintetizador.
              </p>
              <button
                className="gc-btn gc-btn-primary h-9 px-3 text-xs"
                onClick={() => {
                  const pad = { id: crypto.randomUUID(), label: "Nuevo", mode: "synth", f: 440, type: "square", t: 0.15 };
                  savePads([...pads, pad]);
                  setEditingPad(pad.id);
                }}
              >
                <Plus size={14} /> Añadir pad
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {pads.map((p) => (
                <div key={p.id} className="relative">
                  <button className="gc-pad w-full" onClick={() => playPad(p)}>
                    <SpeakerHigh size={18} />
                    {p.label}
                    <span className="text-[9px] opacity-60">{p.mode === "clip" ? "clip" : p.type}</span>
                  </button>
                  <div className="flex gap-1 mt-1">
                    <button className="gc-chip flex-1 justify-center" onClick={() => setEditingPad(editingPad === p.id ? null : p.id)}>
                      <PencilSimple size={11} /> Editar
                    </button>
                    <button className="gc-chip text-rose-500" onClick={() => { savePads(pads.filter((x) => x.id !== p.id)); if (editingPad === p.id) setEditingPad(null); }}>
                      <Trash size={11} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {editingPad && pads.find((p) => p.id === editingPad) && (
              <PadEditor
                pad={pads.find((p) => p.id === editingPad)}
                samples={samples}
                onChange={(next) => savePads(pads.map((x) => x.id === next.id ? next : x))}
                onClose={() => setEditingPad(null)}
              />
            )}
          </div>
        )}

        {sub === "mixer" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GlassCard>
              <div className="font-semibold mb-3 flex items-center gap-2"><Waves size={16} /> Mood de sesión</div>
              {Object.entries({ low: "Graves / amenaza", mid: "Presencia", high: "Brillo UI", space: "Espacio / cueva" }).map(([k, label]) => (
                <label key={k} className="block text-xs mb-3" style={{ color: "var(--gc-muted)" }}>
                  {label}
                  <input
                    className="w-full mt-1"
                    type="range"
                    min="0"
                    max="100"
                    value={mood[k]}
                    onChange={(e) => setMood({ ...mood, [k]: Number(e.target.value) })}
                  />
                </label>
              ))}
              <div className="gc-mood-strip mt-2" style={{ "--a": mood.low, "--b": mood.high }} />
            </GlassCard>
            <GlassCard>
              <div className="font-semibold mb-2 flex items-center gap-2"><MusicNotes size={16} /> Consejo de mezcla</div>
              <ul className="text-sm space-y-2" style={{ color: "var(--gc-muted)" }}>
                <li>SFX cortos a −6 dB para no tapar música.</li>
                <li>Ambiente en loop con fade de 400 ms.</li>
                <li>Voces 2–4 semitonos abajo para bosses.</li>
                <li>Un stinger de 1 s al recoger raro ({mood.high > 60 ? "brillante" : "sordo"}).</li>
              </ul>
            </GlassCard>
          </div>
        )}

        {sub === "seq" && (
          <div className="gc-tool">
            <AudioPanel project={project} onChange={onChange} />
          </div>
        )}
      </div>
    </div>
  );
}

function PadEditor({ pad, samples, onChange, onClose }) {
  const set = (patch) => onChange({ ...pad, ...patch });
  const assignFile = async (files) => {
    const file = files[0];
    if (!file) return;
    const data_url = await new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.readAsDataURL(file);
    });
    set({ mode: "clip", data_url, label: pad.label || file.name.replace(/\.[^.]+$/, "") });
  };
  return (
    <GlassCard className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sm">Editar pad</div>
        <button className="gc-chip" onClick={onClose}>Cerrar</button>
      </div>
      <label className="text-xs block" style={{ color: "var(--gc-muted)" }}>
        Nombre
        <input className="gc-input h-9 px-3 mt-1 w-full" value={pad.label || ""} onChange={(e) => set({ label: e.target.value })} />
      </label>
      <label className="text-xs block" style={{ color: "var(--gc-muted)" }}>
        Etiqueta (motor: jump, hit, pickup…)
        <select className="gc-input h-9 px-3 mt-1 w-full" value={pad.tag || pad.id || ""} onChange={(e) => set({ tag: e.target.value })}>
          {AUDIO_TAGS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </label>
      <div className="flex gap-2">
        <button className={"gc-chip " + (pad.mode !== "clip" ? "gc-chip-on" : "")} onClick={() => set({ mode: "synth" })}>Sintetizador</button>
        <button className={"gc-chip " + (pad.mode === "clip" ? "gc-chip-on" : "")} onClick={() => set({ mode: "clip" })}>Clip propio</button>
      </div>
      {pad.mode === "clip" ? (
        <div className="space-y-2">
          <select
            className="gc-input h-9 px-3 w-full text-sm"
            value={pad.clip_id || ""}
            onChange={(e) => {
              const clip = samples.find((s) => s.id === e.target.value);
              set({ clip_id: e.target.value, data_url: clip?.data_url || pad.data_url });
            }}
          >
            <option value="">— biblioteca —</option>
            {samples.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <FileDropzone accept="audio/*" onFiles={assignFile} label="Subir sonido al pad" />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 text-xs">
          <label>Hz
            <input type="number" className="gc-input h-8 px-2 w-full mt-1" value={pad.f || 440} onChange={(e) => set({ f: Number(e.target.value) })} />
          </label>
          <label>Tipo
            <select className="gc-input h-8 px-2 w-full mt-1" value={pad.type || "square"} onChange={(e) => set({ type: e.target.value })}>
              <option value="sine">sine</option>
              <option value="square">square</option>
              <option value="sawtooth">sawtooth</option>
              <option value="triangle">triangle</option>
            </select>
          </label>
          <label>Duración
            <input type="number" step="0.05" className="gc-input h-8 px-2 w-full mt-1" value={pad.t || 0.15} onChange={(e) => set({ t: Number(e.target.value) })} />
          </label>
        </div>
      )}
    </GlassCard>
  );
}
